import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UserProfileShareService } from '../../../../services/shared-service/user-profile-share.service';
import { CLOUD_DATA_SERVICE, CloudDataService } from '../../../../services/cloud/cloud-data.service';
import { AdminMandant, Employee, EmployeeDetails, EmployeeUpdateDetails } from '../../../../models/cloud/cloud.model';
import { combineLatest, Subscription } from 'rxjs';
import { HideLoader, ShowLoader } from '../../../store/loader/loader.actions';
import { Store } from '@ngxs/store';
import { RESOURCES_DATA_SERVICE, ResourcesDataService } from '../../../../services/resources/resources-data.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'fi-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  standalone: false,
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  employeeDetails: EmployeeDetails;
  mandant?: AdminMandant;
  languages = new Map<string, string>();
  selectedOrgUnitText = '';
  selectedOrgUnitsText = '';
  selectedOrgUnits = [];
  subscription?: Subscription;

  body: EmployeeUpdateDetails = {
    UserId: -1,
    MainOunId: -1,
    ActiveOuns: [],
    ActiveRoles: [],
    ActiveAreas: [],
    ProfileData: {
      NotificationsEnabled: false,
      AppointmentMailEnabled: false,
      ReminderMailEnabled: false,
      GvlMailEnabled: false,
      ExternalMailCCEnabled: false,
      CompletedMailEnabled: false,
      LeadMailEnabled: false,
    }
  };

  notificationCheckboxes = [
    {snippet: 'USER_PROFILE_EDIT_HEADLINE_NOTIFICATIONS', property: 'NotificationsEnabled'},
    {snippet: 'USER_PROFILE_EDIT_HEADLINE_PUSH', property: 'LeadMailEnabled'},
    {snippet: 'USER_PROFILE_EDIT_HEADLINE_APPOINTMENT_NOTIFICATION', property: 'AppointmentMailEnabled'},
    {snippet: 'USER_PROFILE_EDIT_HEADLINE_NOTIFICATION_LEAD_REMINDER', property: 'ReminderMailEnabled'},
    {snippet: 'USER_PROFILE_EDIT_HEADLINE_NOTIFICATION_WEEKLY_RECAP_GVL', property: 'GvlMailEnabled'},
    {snippet: 'USER_PROFILE_EDIT_HEADLINE_NOTIFICATION_CLOSE_REQUESTS', property: 'CompletedMailEnabled'},
    {snippet: 'USER_PROFILE_EDIT_HEADLINE_NOTIFICATION_EXTERNALLY_ASSIGNED', property: 'ExternalMailCCEnabled'},
  ];

  constructor(
      @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService,
      @Inject(CLOUD_DATA_SERVICE) private cloudDataService: CloudDataService,
    private readonly sharedProfileService: UserProfileShareService,
    private store: Store,
    private route: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.resourcesDataService.getResourceLanguages().subscribe(({ success, data }) => {
      if (success) {
        this.languages = new Map(data.map(x => [x.IsoCode, x.Name]));
      }
    });
    this.subscription = combineLatest([this.sharedProfileService.getProfile(), this.sharedProfileService.getAdminMandant()])
        .subscribe(([profile, mandant]) => {
      if (mandant && profile) {
        this.mandant = mandant;
        setTimeout(() => {
          this.subscription?.unsubscribe();
          this.subscription = null;
        });

        this.store.dispatch(new ShowLoader());

        this.openUserDetails(parseInt(this.route.snapshot.paramMap.get('id')));
      }
    })
  }

  ngOnDestroy(): void {
    this.store.dispatch(new HideLoader());
    this.subscription?.unsubscribe();
  }

  openUserDetails(employeeId: number): void {
    this.store.dispatch(new ShowLoader());

    this.cloudDataService.getEmployee(employeeId, this.mandant.Id).subscribe(({success, data, error}) => {
      if (success) {
        this.employeeDetails = data;
        this.body.UserId = data.ProfileData.User.Id;
        this.body.MainOunId = data.MainOunId ?? -1;
        this.body.ActiveRoles = data.ActiveRoles.filter(x => x.Enabled).map(x => x.RoleId);
        this.body.ActiveAreas = data.ActiveAreas.filter(x => x.Enabled).map(x => x.Id);
        this.body.ActiveOuns = data.ActiveOuns.filter(x => x.Enabled).map(x => x.Id);
        this.body.ProfileData.NotificationsEnabled = data.ProfileData.User.NotificationsEnabled;
        this.body.ProfileData.AppointmentMailEnabled = data.ProfileData.User.AppointmentMailEnabled;
        this.body.ProfileData.ReminderMailEnabled = data.ProfileData.User.ReminderMailEnabled;
        this.body.ProfileData.GvlMailEnabled = data.ProfileData.User.GvlMailEnabled;
        this.body.ProfileData.ExternalMailCCEnabled = data.ProfileData.User.ExternalMailCCEnabled;
        this.body.ProfileData.CompletedMailEnabled = data.ProfileData.User.CompletedMailEnabled;
        this.body.ProfileData.LeadMailEnabled = data.ProfileData.User.LeadMailEnabled;
        this.selectedOrgUnits = this.employeeDetails.ActiveOuns.filter(x => this.body.ActiveOuns.includes(x.Id));
        this.updateSelectedTexts();
      }

      this.store.dispatch(new HideLoader());

    })
  }

  updateOrgUnit(value: string): void {
    this.body.MainOunId = +value;
    this.updateSelectedTexts();
  }

  updateOrgUnits(values: string[]): void {
    this.body.ActiveOuns = values.map(x => +x);
    this.selectedOrgUnits = this.employeeDetails.ActiveOuns.filter(x => this.body.ActiveOuns.includes(x.Id));
    this.updateSelectedTexts();
  }

  updateSelectedTexts(): void {
    this.selectedOrgUnitText = this.employeeDetails.ActiveOuns.find(x => x.Id === this.body.MainOunId)?.Description ?? '';
    this.selectedOrgUnitsText = this.employeeDetails.ActiveOuns.filter(x => this.body.ActiveOuns.includes(x.Id)).map(x => x.Description).join(', ')
  }

  onSave(): void {
    this.body.ActiveRoles = this.employeeDetails.ActiveRoles.filter(x => x.Enabled).map(x => x.RoleId);
    this.body.ActiveAreas = this.employeeDetails.ActiveAreas.filter(x => x.Enabled).map(x => x.Id);

    this.store.dispatch(new ShowLoader());

    this.cloudDataService.updateEmployee(this.body, this.mandant.Id).subscribe(({success, data, error}) => {
      this.store.dispatch(new HideLoader());

    })
  }
}
